import {
  gapEvidenceCoverage, gapFactorProgress,
  type GapIndicator, type Gaps as GapsContent,
} from '@ads/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { Tabs } from '../components/Tabs';
import { useConsolidate, useEvidence, useMaterialAction, useSaveTool, useTool } from '../lib/queries';
import { GapsDashboard } from './GapsDashboard';
import { EvidenceModal } from './EvidenceModal';

/* ─────────────────────────────────────────────────────────────────────
   Mapa de Gaps. Preenchimento: a tabela é editável célula a célula e se
   salva sozinha; a evidência de cada indicador abre um painel para
   enviar, ver e excluir documentos. Consolidado: o painel de leitura,
   com as contas refeitas a cada edição e a leitura do agente logo depois.
   ───────────────────────────────────────────────────────────────────── */

const COLS: Array<[string, number]> = [
  ['Indicador', 190], ['O que fazemos?', 200], ['Por quê?', 190], ['Onde?', 120], ['Quem?', 130],
  ['Quando?', 128], ['Como?', 200], ['Quanto?', 112], ['Evidência', 118], ['Maturidade', 124], ['Meta', 70],
];
const SAVE_MS = 800;
const newId = () => (globalThis.crypto?.randomUUID?.() ?? `i-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function Gaps() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', 'Mapa de Gaps'], j: .22 });
  const nav = useNavigate();
  const tool = useTool('gaps');
  const save = useSaveTool('gaps');
  const evidence = useEvidence();
  const consolidate = useConsolidate();
  const act = useMaterialAction();
  const [tab, setTab] = useState<'fill' | 'sum'>('fill');
  const [fi, setFi] = useState(0);
  const [local, setLocal] = useState<GapsContent | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [modal, setModal] = useState<{ id: string; name: string; factor: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pending = useRef<GapsContent | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: false });

  useEffect(() => { if (tool.data && !local) setLocal(tool.data.content); }, [tool.data, local]);

  /* evidências: o número de cada indicador vem dos arquivos, não do que está na tela */
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of evidence.data ?? []) if (e.ref?.startsWith('gaps:')) m.set(e.ref.slice(5), (m.get(e.ref.slice(5)) ?? 0) + 1);
    return m;
  }, [evidence.data]);
  const content = useMemo<GapsContent | null>(() => (local ? {
    factors: local.factors.map(f => ({ ...f, indicators: f.indicators.map(i => ({ ...i, evidence: evidence.data ? counts.get(i.id ?? '') ?? 0 : i.evidence })) })),
  } : null), [local, counts, evidence.data]);
  const factors = content?.factors ?? [];
  const factor = factors[fi];

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setSaveState('saving');
    save.mutate(next, {
      onSuccess: () => setSaveState(s => (pending.current ? s : 'saved')),
      onError: () => { setSaveState('error'); pending.current ??= next; },
    });
  }, [save]);
  const commit = (next: GapsContent) => {
    setLocal(next);
    pending.current = next;
    setSaveState('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_MS);
  };
  useEffect(() => () => { flush(); }, [flush]);

  const patch = (i: number, p: Partial<GapIndicator>) => {
    if (!local) return;
    commit({ factors: local.factors.map((f, j) => (j !== fi ? f : { ...f, indicators: f.indicators.map((ind, k) => (k === i ? { ...ind, ...p } : ind)) })) });
  };
  const addRow = () => {
    if (!local) return;
    const row: GapIndicator = { id: newId(), name: '', description: '', what: '', why: '', where: '', who: '', when: '', how: '', cost: null, evidence: 0, maturity: 0, target: 0 };
    commit({ factors: local.factors.map((f, j) => (j !== fi ? f : { ...f, indicators: [...f.indicators, row] })) });
    requestAnimationFrame(() => wrap.current?.querySelector<HTMLTextAreaElement>('tbody tr:last-child textarea')?.focus());
  };
  const removeRow = (i: number) => {
    if (!local) return;
    /* as evidências do indicador saem junto */
    const id = local.factors[fi]?.indicators[i]?.id;
    for (const e of evidence.data ?? []) if (id && e.ref === `gaps:${id}`) act.mutate({ id: e.id, action: 'delete' });
    commit({ factors: local.factors.map((f, j) => (j !== fi ? f : { ...f, indicators: f.indicators.filter((_, k) => k !== i) })) });
  };

  /* os dois botões de rolagem e o estado de fim */
  useEffect(() => {
    const w = wrap.current;
    if (!w) return;
    const upd = () => setEdge({ start: w.scrollLeft < 4, end: w.scrollLeft > w.scrollWidth - w.clientWidth - 4 });
    upd();
    w.addEventListener('scroll', upd, { passive: true });
    const ro = new ResizeObserver(upd); ro.observe(w);
    return () => { w.removeEventListener('scroll', upd); ro.disconnect(); };
  }, [tab, fi, factors.length]);
  const scroll = (dir: number) => { const w = wrap.current; if (w) w.scrollBy({ left: dir * Math.round(w.clientWidth * .62), behavior: 'smooth' }); };

  const coverage = factor ? Math.round(gapEvidenceCoverage(factor) * 100) : 0;
  const status = saveState === 'saving' ? 'Salvando…' : saveState === 'saved' ? 'Salvo' : saveState === 'error' ? 'Não salvou — tentando de novo' : '';

  return (
    <section className="view on" data-v="gaps">
      <button className="back" type="button" onClick={() => { flush(); nav('/strategy'); }}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 01 / 10</b></div>
          <h1>Mapa de Gaps</h1>
          <p className="lede">Avalie os indicadores de cada fator e identifique os principais gaps da organização.</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
          <Tabs options={[['fill', 'Preenchimento'], ['sum', 'Consolidado']] as const} value={tab} onChange={setTab} />
        </div>
      </div>

      <div className="factors" role="tablist">
        {factors.map((f, i) => {
          const p = gapFactorProgress(f);
          const [first, ...rest] = f.name.split(' ');
          return (
            <button key={f.key} className="fac" type="button" role="tab" aria-selected={tab === 'fill' && i === fi} style={{ '--p': p } as CSSProperties}
              onClick={() => { setFi(i); setTab('fill'); }}>
              <b>{first}<br />{rest.join(' ')}</b><span>{Math.round(p * 100)}%</span>
            </button>
          );
        })}
      </div>

      {!content ? <div className="loading-line" /> : tab === 'fill' ? (
        factor && (
          <>
            <div className="blocks-head" style={{ alignItems: 'flex-end' }}>
              <div>
                <h2>{factor.name}</h2>
                <p className="subhead">Avalie o estado atual e o desejado de cada indicador. Tudo é editável e se salva sozinho; a maturidade vai de 1 a 5.</p>
              </div>
              <span className="meta cover">Cobertura das evidências
                <span className="mini-track"><i style={{ width: `${coverage}%` }} /></span>
                <b>{coverage}%</b>
                <span className="tscroll">
                  <button type="button" disabled={edge.start} onClick={() => scroll(-1)} aria-label="Rolar tabela para a esquerda"><Ic.ChevronLeft sw={2.4} /></button>
                  <button type="button" disabled={edge.end} onClick={() => scroll(1)} aria-label="Rolar tabela para a direita"><Ic.ChevronRight sw={2.4} /></button>
                </span>
              </span>
            </div>

            <div className="tablewrap" ref={wrap}>
              <table className="dense gtable">
                <thead><tr>{COLS.map(([c, w]) => <th key={c} style={{ minWidth: w }}>{c}</th>)}</tr></thead>
                <tbody>
                  {factor.indicators.map((ind, i) => (
                    <Row key={ind.id ?? i} ind={ind} onPatch={p => patch(i, p)} onRemove={() => removeRow(i)}
                      onEvidence={() => setModal({ id: ind.id!, name: ind.name || 'Indicador sem nome', factor: factor.name })} />
                  ))}
                </tbody>
              </table>
            </div>
            <button className="addlink" type="button" onClick={addRow}><Ic.Plus />Adicionar indicador em {factor.name}</button>
          </>
        )
      ) : (
        <GapsDashboard content={content} />
      )}

      {modal && (
        <EvidenceModal onClose={() => setModal(null)} target={{
          ref: `gaps:${modal.id}`, title: modal.name, kicker: `Evidências · ${modal.factor}`,
          lede: 'Documentos de acompanhamento deste indicador — atas, relatórios, planilhas, fotos. O ADS lê cada um e usa como prova na leitura do consolidado.',
          empty: 'Um indicador com evidência pesa mais na maturidade real calculada pelo ADS.',
        }} />
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy'); }}>Salvar e sair</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending}
          onClick={() => { flush(); consolidate.mutate(undefined, { onSettled: () => nav('/dna') }); }}>
          {consolidate.isPending ? 'Consolidando o DNA…' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}

/* ── uma linha: cada célula é um campo ────────────────────────────── */

function Row({ ind, onPatch, onRemove, onEvidence }: {
  ind: GapIndicator; onPatch: (p: Partial<GapIndicator>) => void; onRemove: () => void; onEvidence: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (!confirm) return; const t = setTimeout(() => setConfirm(false), 3500); return () => clearTimeout(t); }, [confirm]);
  const name = ind.name || 'este indicador';
  return (
    <tr>
      <td className="g-name">
        <div className="g-name-in">
          <Cell value={ind.name} label="Indicador" placeholder="Nome do indicador" onChange={v => onPatch({ name: v })} strong />
          {ind.description && <span className="g-help" tabIndex={0} role="img" aria-label={ind.description} data-tip={ind.description}><Ic.Help /></span>}
        </div>
        {confirm
          ? <button type="button" className="g-del" onClick={onRemove}>{ind.evidence ? `Excluir com ${ind.evidence} ${ind.evidence === 1 ? 'evidência' : 'evidências'}` : 'Excluir indicador'}</button>
          : <button type="button" className="g-x" onClick={() => setConfirm(true)} aria-label={`Remover ${name}`}><Ic.Close /></button>}
      </td>
      <td><Cell value={ind.what} label={`O que fazemos — ${name}`} placeholder="O que fazemos" onChange={v => onPatch({ what: v })} /></td>
      <td><Cell value={ind.why} label={`Por quê — ${name}`} placeholder="Por quê" onChange={v => onPatch({ why: v })} /></td>
      <td><Cell value={ind.where} label={`Onde — ${name}`} placeholder="Área" onChange={v => onPatch({ where: v })} /></td>
      <td><Cell value={ind.who} label={`Quem — ${name}`} placeholder="Responsável" onChange={v => onPatch({ who: v })} /></td>
      <td><input className="gcell gdate" type="date" value={ind.when} aria-label={`Quando — ${name}`} onChange={e => onPatch({ when: e.target.value })} /></td>
      <td><Cell value={ind.how} label={`Como — ${name}`} placeholder="Como" onChange={v => onPatch({ how: v })} /></td>
      <td><Money value={ind.cost} label={`Quanto — ${name}`} onChange={v => onPatch({ cost: v })} /></td>
      <td>
        <button type="button" className={`evid g-evid${ind.evidence ? '' : ' none'}`} onClick={onEvidence} disabled={!ind.id}>
          <Ic.Clip />{ind.evidence ? `${ind.evidence} arquivo${ind.evidence > 1 ? 's' : ''}` : 'Anexar'}
        </button>
      </td>
      <td>
        <span className="mat" role="radiogroup" aria-label={`Maturidade de ${name}`}>
          {[1, 2, 3, 4, 5].map(l => (
            <i key={l} role="radio" aria-checked={ind.maturity === l} tabIndex={0}
              className={`${l <= ind.maturity ? 'on' : ''}${l === ind.target ? ' tgt' : ''}`}
              title={l === ind.target ? `Meta: ${l}` : String(l)}
              onClick={() => onPatch({ maturity: ind.maturity === l ? l - 1 : l })}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPatch({ maturity: ind.maturity === l ? l - 1 : l }); } }} />
          ))}
          <b>{ind.maturity || '—'}</b>
        </span>
      </td>
      <td>
        <select className="gcell gsel" value={ind.target} aria-label={`Meta de maturidade — ${name}`} onChange={e => onPatch({ target: Number(e.target.value) })}>
          <option value={0}>—</option>
          {[1, 2, 3, 4, 5].map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </td>
    </tr>
  );
}

function Cell({ value, onChange, label, placeholder, strong }: { value: string; onChange: (v: string) => void; label: string; placeholder: string; strong?: boolean }) {
  return (
    <textarea className={`gcell${strong ? ' strong' : ''}`} rows={1} value={value} placeholder={placeholder} aria-label={label}
      maxLength={strong ? 120 : 400} onChange={e => onChange(e.target.value)} />
  );
}

const fmt = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
function Money({ value, onChange, label }: { value: number | null; onChange: (v: number | null) => void; label: string }) {
  const [editing, setEditing] = useState<string | null>(null);
  const shown = editing ?? (value == null ? '' : fmt.format(value));
  return (
    <span className="gmoney">
      <em>R$</em>
      <input className="gcell" inputMode="numeric" value={shown} placeholder="—" aria-label={label}
        onFocus={() => setEditing(value == null ? '' : String(value))}
        onChange={e => {
          setEditing(e.target.value);
          const digits = (e.target.value.split(',')[0] ?? '').replace(/[^\d]/g, '');
          onChange(digits ? Number(digits) : null);
        }}
        onBlur={() => setEditing(null)} />
    </span>
  );
}
